import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Link, Typography, Hidden, ButtonBase, useMediaQuery } from '@material-ui/core';
import PhoneIcon from '@material-ui/icons/Phone';

// CONSTANTS
import { ROUTE_RESULTS_INTERNET_OPTION } from '@/constants/routes';
import { LOCATIONS_RESULTS_PAGE } from '@/constants/locations';

// UTILITY
import { updateUrl } from '@/utility/common';
import {
  analyticsClickInternetOption,
  externalLink,
  getExternalUrlParams,
} from '@/utility/analytics';
import { getLink } from '@/utility/routes';
import { formatPhoneNumber } from '@/utility/content';
import { formatPrice } from '@/utility/currency';

// HOC
import CollapseHOC from '@/hoc/Collapse';

// COMPONENTS
import RouterLink from '@/components/Link';
import Loader from '@/components/Loader';
import DialogInfo from '@/components/Dialogs/DialogInfo';
import ReactHtml from '@/components/ReactHtml';

// STYLES
import { Container, Price } from './styles';

const InternetOption = props => {
  const {
    data,
    pricePerMonth,
    onInternalLinkClick,
    onExternalLinkClick,
    collapsible,
    loading,
    onClaim,
    analyticsLocation,
  } = props;
  const [t] = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const { isIframe, unique_id } = useSelector(({ app, auth }) => ({
    isIframe: app.isIframe,
    unique_id: auth.unique_id,
  }));
  const downXs = useMediaQuery(({ breakpoints }) => breakpoints.down('xs'));
  const isCollapse = collapsible && downXs;
  const externalUrlParams = React.useMemo(() => getExternalUrlParams({ unique_id }), [unique_id]);
  const internetOptionLink = React.useMemo(
    () =>
      getLink(ROUTE_RESULTS_INTERNET_OPTION, {
        unique_id,
        internet_option_id: data.id,
      }),
    [unique_id, data.id],
  );
  const description = data.product ? data.product.result_description : data.text;
  const hasButtonTempl = description.includes('<button>');
  const showButton = !!data.show_button;
  const isPartnerOffer = data.type === 'partner_offer';
  const showPartnerOfferButton = isPartnerOffer && !data.status;
  const hasAd = !!data.ad_text;

  let benefits = data.benefits;

  if (data.product && data.product.benefits) {
    benefits = data.product.benefits;
  }

  const toggleExpand = React.useCallback(() => {
    if (isCollapse) {
      setExpanded(state => !state);
    }
  }, [isCollapse]);

  const handleModal = React.useCallback(() => {
    setOpenModal(state => !state);
  }, []);

  const sendIOAnalytics = React.useCallback(() => {
    let url = '';

    if (data.show_internet_option_page) {
      url = window.location.origin + internetOptionLink;
    } else {
      url = data.button_url ? updateUrl(data.button_url, externalUrlParams) : '';
    }

    const properties = {
      provider_name: data.internal_name,
      url,
      id: data.id,
      form_submission_internet_option_id: data.form_submission_internet_option_id || '',
      unique_id,
    };

    analyticsClickInternetOption(properties);
  }, [data, window.location.origin, internetOptionLink, externalUrlParams, unique_id]);

  const handleExternalLink = React.useCallback(
    product => {
      const href = updateUrl(product.button_url, externalUrlParams);

      const properties = {
        href,
        location: 'Find My Bundle Results Page',
        service_id: product.internet_option_id,
        service_name: product.ProductName,
        type: 'text',
        database_id: data.id,
        other: 'Internet Option',
      };

      externalLink(properties);
    },
    [data.id, externalUrlParams],
  );

  const handleAnalytics = React.useCallback(
    cb => {
      if (cb) {
        cb(data);
      } else {
        sendIOAnalytics();
      }
    },
    [data],
  );

  const handleInternalClick = React.useCallback(() => {
    handleAnalytics(onInternalLinkClick);
  }, [onInternalLinkClick]);

  const handleExternalClick = React.useCallback(() => {
    handleAnalytics(onExternalLinkClick);
  }, [onExternalLinkClick]);

  const getPrice = React.useCallback(
    price => {
      return price && Math.ceil(price) !== 0 ? (
        <Typography className="p4 price__price" component="span">
          ${formatPrice(price)}
          <span className="p4 text-color--grey">/{t('common.month')}</span>
        </Typography>
      ) : null;
    },
    [t],
  );

  const priceContent = React.useMemo(() => {
    const product = data.product;
    let content = null;

    if (product) {
      const ibexRegularPrice = +product?.BasePrice?.RegularAmount || 0;
      const ibexInitialPrice = product?.BasePrice?.InitialAmount || 0;
      let locaclPriceContent = getPrice(ibexRegularPrice || ibexInitialPrice);

      if (ibexRegularPrice) {
        locaclPriceContent = (
          <div className="price-section">
            <div className="price-section__old">{locaclPriceContent}</div>
            <div className="price-section__deal">
              {getPrice(ibexInitialPrice)}{' '}
              {!!product.BasePrice.InitialAmountLengthMonths && (
                <Typography className="p4" component="span">
                  {t('common.durationMonths', {
                    duration: product.BasePrice.InitialAmountLengthMonths,
                  })}
                </Typography>
              )}
            </div>
          </div>
        );
      }

      content = locaclPriceContent;
    } else if (data.price_text) {
      content = (
        <Typography className="p4 price__price" component="span">
          {Number.isNaN(+data.price_text) ? data.price_text : `$${+data.price_text}`}
          {pricePerMonth && <span className="p4 text-color--grey">/{t('common.month')}</span>}
        </Typography>
      );
    }

    return content ? <Price className="internet-option__price-wrap price">{content}</Price> : null;
  }, [data.product, data.price_text]);

  const cta = React.useMemo(() => {
    const product = data.product;
    let content = null;

    if (product) {
      content = (
        <>
          {!!product.phone && (
            <Button
              className="sizeExtraLarge"
              component="a"
              href={`tel:${product.phone}`}
              target="_blank"
              color="primary"
              startIcon={<PhoneIcon />}
            >
              {formatPhoneNumber(product.phone)}
            </Button>
          )}
          {showButton && (
            <Button
              className="sizeExtraLarge"
              component="a"
              onClick={() => handleExternalLink(product)}
              href={updateUrl(product.button_url)}
              target="_blank"
              color="primary"
              variant="outlined"
            >
              {t('common.purchase')}
            </Button>
          )}
        </>
      );
    } else {
      content = (
        <>
          {!!data.phone && (
            <Button
              className="sizeExtraLarge"
              component="a"
              href={`tel:${data.phone}`}
              target="_blank"
              color="primary"
              startIcon={<PhoneIcon />}
            >
              {formatPhoneNumber(data.phone)}
            </Button>
          )}
          {data.show_internet_option_page ? (
            <Button
              size="medium"
              color="primary"
              variant="outlined"
              component={RouterLink}
              to={internetOptionLink}
              target={isIframe ? '_self' : '_blank'}
              className="visit-site"
              onClick={handleInternalClick}
              location={analyticsLocation || LOCATIONS_RESULTS_PAGE}
            >
              {data.button_text || t('buttons.visitSite')}
            </Button>
          ) : showPartnerOfferButton ? (
            <Button
              size="medium"
              color="primary"
              variant="outlined"
              {...(data.button_url
                ? {
                    href: updateUrl(data.button_url, externalUrlParams),
                    target: isIframe ? '_self' : '_blank',
                    className: 'visit-site',
                  }
                : { onClick: onClaim, className: 'claim' })}
            >
              {data.button_text || t('buttons.claim')}
            </Button>
          ) : (
            showButton && (
              <Button
                size="medium"
                color="primary"
                variant="outlined"
                href={updateUrl(data.button_url, externalUrlParams)}
                target="_blank"
                onClick={handleExternalClick}
              >
                {data.button_text || t('buttons.visitSite')}
              </Button>
            )
          )}
        </>
      );
    }

    return content ? <div className="internet-option__actions">{content}</div> : null;
  }, [data, externalUrlParams, isIframe, onClaim]);

  function getTitle(text) {
    return (
      <Typography variant={isCollapse ? 'h5' : 'h4'} className="m-b-5">
        {text}
      </Typography>
    );
  }

  const getTitleContent = (className = '') => (
    <div className={className}>
      {!!data.title && getTitle(data.title)}
      {priceContent}
    </div>
  );

  return (
    <Container
      className={clsx('internet-option', {
        'internet-option--collapsible': isCollapse,
        'internet-option--ad': hasAd,
      })}
      elevation={0}
    >
      {hasAd && (
        <div className="internet-option__ad">
          <ReactHtml className="markdown ad-text">{data.ad_text}</ReactHtml>
        </div>
      )}
      <div className="internet-option__wrap">
        <div className="internet-option__left">
          <ButtonBase
            component={isCollapse ? 'button' : 'div'}
            disableRipple
            className="internet-option__main-info"
            onClick={toggleExpand}
          >
            {isCollapse && <i className="icon-chevron-right" />}
            <div className="internet-option__image">
              <img src={data.image} alt={data.internal_name || ''} />
            </div>
            {downXs
              ? getTitle(data.mobile_banner_title || t('internetOption.title'))
              : getTitleContent()}
          </ButtonBase>
          <CollapseHOC showCollapse={isCollapse} open={expanded}>
            <div className="internet-option__content">
              <div className="internet-option__description">
                {downXs && getTitleContent('m-b-10')}
                {!!description && (
                  <ReactHtml
                    className="markdown"
                    components={{
                      button: btnProps => (
                        <Link
                          {...btnProps}
                          className="internet-option__offer-details"
                          component="button"
                          onClick={handleModal}
                        />
                      ),
                    }}
                  >
                    {description}
                  </ReactHtml>
                )}
                {!!data.disclosure_text && !hasButtonTempl && (
                  <Link className="internet-option__offer-details" onClick={handleModal}>
                    {' '}
                    {t('buttons.offerDetails')}
                  </Link>
                )}
              </div>
              <Hidden mdUp>
                {!!benefits && <ReactHtml className="markdown benefits">{benefits}</ReactHtml>}
              </Hidden>
              {cta}
              {!!data.product && (
                <>
                  {data.show_internet_option_page ? (
                    <Link
                      className="view-offers-link"
                      to={internetOptionLink}
                      component={RouterLink}
                      onClick={handleInternalClick}
                      target={isIframe ? '_self' : '_blank'}
                      location={analyticsLocation || LOCATIONS_RESULTS_PAGE}
                    >
                      {data.button_text || t('buttons.visitSite')}
                    </Link>
                  ) : (
                    showButton && (
                      <Link
                        className="view-offers-link"
                        href={updateUrl(data.button_url, externalUrlParams)}
                        onClick={handleExternalClick}
                      >
                        {data.button_text || t('buttons.visitSite')}
                      </Link>
                    )
                  )}
                </>
              )}
            </div>
          </CollapseHOC>
        </div>
        <Hidden smDown>
          <div
            className="internet-option__right"
            style={
              data.background_image ? { backgroundImage: `url(${data.background_image})` } : {}
            }
          >
            {!!benefits && <ReactHtml className="markdown benefits">{benefits}</ReactHtml>}
          </div>
        </Hidden>
      </div>
      <Loader active={loading} />
      {!!data.disclosure_text && (
        <DialogInfo
          open={openModal}
          onClose={handleModal}
          title={t('internetOption.disclosure.title')}
        >
          <ReactHtml className={clsx('markdown', downXs ? 'p4' : 'p3')}>
            {data.disclosure_text}
          </ReactHtml>
        </DialogInfo>
      )}
    </Container>
  );
};

InternetOption.defaultProps = {
  pricePerMonth: false,
  onInternalLinkClick: null,
  onExternalLinkClick: null,
  collapsible: false,
  loading: false,
  onClaim: () => {},
  analyticsLocation: '',
};

InternetOption.propTypes = {
  data: PropTypes.object.isRequired,
  pricePerMonth: PropTypes.bool,
  collapsible: PropTypes.bool,
  loading: PropTypes.bool,
  onClaim: PropTypes.func,
  onInternalLinkClick: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf([null])]),
  onExternalLinkClick: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf([null])]),
  analyticsLocation: PropTypes.string,
};

export default React.memo(InternetOption);
